import { format, subDays, startOfDay, isAfter } from 'date-fns';
import { getQuestionAnswerPlainText, getMatrixPayload } from './matrixAnswerFormat';

const EMPTY_LOCATION = new Set(['', 'Não disponível', 'N/A', 'n/a']);

function trimStr(v) {
  return v != null ? String(v).trim() : '';
}

export function getEntrySurveyNames(entry) {
  const names = new Set();
  entry.answers?.forEach((a) => {
    const n = trimStr(a.nome);
    if (n) names.add(n);
  });
  return [...names];
}

export function getEntryPrimarySurveyName(entry) {
  const names = getEntrySurveyNames(entry);
  return names[0] || 'Sem nome';
}

export function getEntryLocationLabel(entry) {
  const loc = entry.location;
  if (!loc) return '';
  const city = trimStr(loc.city);
  const state = trimStr(loc.state);
  if (city && !EMPTY_LOCATION.has(city)) {
    return state && !EMPTY_LOCATION.has(state) ? `${city}, ${state}` : city;
  }
  if (state && !EMPTY_LOCATION.has(state)) return state;
  return '';
}

export function hasValidLocation(entry) {
  return Boolean(getEntryLocationLabel(entry));
}

export function hasAudio(entry) {
  return Boolean(entry.audioUrl);
}

function entryMatchesSurvey(entry, surveyName) {
  if (!surveyName || surveyName === 'Todos') return true;
  return getEntrySurveyNames(entry).includes(surveyName);
}

function entryMatchesBlock(entry, blockName) {
  if (!blockName || blockName === 'Todos') return true;
  return entry.answers?.some((answer) =>
    answer.questions?.some((q) => trimStr(q.tituloBloco) === blockName)
  );
}

function entryMatchesLocal(entry, localLabel) {
  if (!localLabel || localLabel === 'Todos') return true;
  return getEntryLocationLabel(entry) === localLabel;
}

export function filterResults(results, { survey, block, local } = {}) {
  if (!Array.isArray(results)) return [];
  return results.filter(
    (entry) =>
      entryMatchesSurvey(entry, survey) &&
      entryMatchesBlock(entry, block) &&
      entryMatchesLocal(entry, local)
  );
}

export function collectFilterOptions(results) {
  const surveys = new Set();
  const blocks = new Set();
  const locals = new Set();

  results.forEach((entry) => {
    getEntrySurveyNames(entry).forEach((n) => surveys.add(n));
    entry.answers?.forEach((answer) => {
      answer.questions?.forEach((q) => {
        const b = trimStr(q.tituloBloco);
        if (b) blocks.add(b);
      });
    });
    const loc = getEntryLocationLabel(entry);
    if (loc) locals.add(loc);
  });

  const sortPt = (a, b) => a.localeCompare(b, 'pt-BR');
  return {
    surveys: [...surveys].sort(sortPt),
    blocks: [...blocks].sort(sortPt),
    locals: [...locals].sort(sortPt),
  };
}

function isAnswered(question) {
  const text = getQuestionAnswerPlainText(question);
  return Boolean(text && text.trim());
}

/** Extrai número de satisfação (1–5 ou 1–10) de texto de resposta. */
function parseSatisfactionScore(text) {
  if (!text) return null;
  const t = String(text).trim();
  const direct = Number(t);
  if (Number.isFinite(direct) && direct >= 1 && direct <= 10) return direct;
  const match = t.match(/\b([1-9]|10)\b/);
  if (match) {
    const n = Number(match[1]);
    if (n >= 1 && n <= 10) return n;
  }
  return null;
}

function collectSatisfactionScores(results) {
  const scores = [];
  results.forEach((entry) => {
    entry.answers?.forEach((answer) => {
      answer.questions?.forEach((q) => {
        const text = getQuestionAnswerPlainText(q);
        const score = parseSatisfactionScore(text);
        if (score != null) scores.push(score);
      });
    });
  });
  return scores;
}

export function computeKpis(filteredResults, questionarios = []) {
  const totalRespostas = filteredResults.length;
  const surveyNames = new Set();
  filteredResults.forEach((e) => getEntrySurveyNames(e).forEach((n) => surveyNames.add(n)));

  const totalPesquisas =
    Array.isArray(questionarios) && questionarios.length > 0
      ? questionarios.length
      : surveyNames.size;

  const pesquisasAtivas = Array.isArray(questionarios)
    ? questionarios.filter((q) => q.ativo !== false).length
    : 0;

  const comAudio = filteredResults.filter(hasAudio).length;
  const comLocal = filteredResults.filter(hasValidLocation).length;

  const pctAudio = totalRespostas > 0 ? (comAudio / totalRespostas) * 100 : 0;
  const pctLocal = totalRespostas > 0 ? (comLocal / totalRespostas) * 100 : 0;

  const scores = collectSatisfactionScores(filteredResults);
  const satisfacao =
    scores.length > 0
      ? scores.reduce((a, b) => a + b, 0) / scores.length
      : null;

  return {
    totalPesquisas,
    totalRespostas,
    pesquisasAtivas,
    pctAudio,
    pctLocal,
    satisfacao,
  };
}

function countBy(items, keyFn) {
  const map = new Map();
  items.forEach((item) => {
    const key = keyFn(item) || 'Outros';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function buildChartData(filteredResults) {
  const porPesquisa = countBy(filteredResults, getEntryPrimarySurveyName);

  const blockHits = [];
  filteredResults.forEach((entry) => {
    const blocks = new Set();
    entry.answers?.forEach((answer) => {
      answer.questions?.forEach((q) => {
        const b = trimStr(q.tituloBloco);
        if (b) blocks.add(b);
      });
    });
    if (blocks.size === 0) {
      blockHits.push('Sem bloco');
    } else {
      blocks.forEach((b) => blockHits.push(b));
    }
  });
  const porBloco = countBy(blockHits, (x) => x);

  const porLocal = countBy(filteredResults, (e) => getEntryLocationLabel(e) || 'Sem local');

  const comAudio = filteredResults.filter(hasAudio).length;
  const semAudio = filteredResults.length - comAudio;
  const midias = [
    { name: 'Com áudio', value: comAudio },
    { name: 'Sem áudio', value: semAudio },
  ].filter((d) => d.value > 0);

  const durations = filteredResults
    .map((e) => e.audioDuration)
    .filter((d) => d != null && Number.isFinite(Number(d)) && Number(d) > 0)
    .map(Number);
  const duracaoMediaSegundos =
    durations.length > 0
      ? durations.reduce((a, b) => a + b, 0) / durations.length
      : null;

  const since = startOfDay(subDays(new Date(), 29));
  const byDay = new Map();
  for (let i = 0; i < 30; i++) {
    const day = format(subDays(new Date(), 29 - i), 'yyyy-MM-dd');
    byDay.set(day, 0);
  }
  filteredResults.forEach((entry) => {
    if (!entry.timestamp) return;
    const d = new Date(entry.timestamp);
    if (Number.isNaN(d.getTime()) || !isAfter(d, subDays(since, 1))) return;
    const key = format(d, 'yyyy-MM-dd');
    if (byDay.has(key)) byDay.set(key, byDay.get(key) + 1);
  });
  const temporal = [...byDay.entries()].map(([date, value]) => ({
    date,
    label: format(new Date(date + 'T12:00:00'), 'dd/MM'),
    value,
  }));

  return {
    porPesquisa,
    porBloco,
    porLocal,
    midias,
    duracaoMediaSegundos,
    temporal,
  };
}

function answerTokens(question) {
  const mp = getMatrixPayload(question);
  if (mp && Array.isArray(mp.rows)) {
    return mp.rows
      .map((r) => {
        const col = r.columnLabel != null ? String(r.columnLabel).trim() : '';
        const row = r.rowLabel != null ? String(r.rowLabel).trim() : '';
        if (col && row) return `${row}: ${col}`;
        return col || row;
      })
      .filter(Boolean);
  }
  if (question.questionType === 'checkbox' && Array.isArray(question.options)) {
    return question.options
      .map((o) => (o == null ? '' : String(o).trim()))
      .filter(Boolean);
  }
  const text = getQuestionAnswerPlainText(question);
  return text ? [text] : [];
}

/**
 * Análise do questionário: participação por pergunta, top perguntas e distribuição de opções.
 * @param {object[]} filteredResults
 * @param {string|null} surveyFilter - se definido, restringe às respostas dessa pesquisa
 */
export function buildQuestionnaireAnalysis(filteredResults, surveyFilter = null) {
  const scoped =
    surveyFilter && surveyFilter !== 'Todos'
      ? filteredResults.filter((e) => entryMatchesSurvey(e, surveyFilter))
      : filteredResults;

  const total = scoped.length;
  /** @type {Map<string, { question: string, answered: number, optionCounts: Map<string, number> }>} */
  const byQuestion = new Map();

  scoped.forEach((entry) => {
    entry.answers?.forEach((answer) => {
      if (surveyFilter && surveyFilter !== 'Todos') {
        const n = trimStr(answer.nome);
        if (n && n !== surveyFilter) return;
      }
      answer.questions?.forEach((q) => {
        const qText = trimStr(q.question) || 'Pergunta sem título';
        if (!byQuestion.has(qText)) {
          byQuestion.set(qText, {
            question: qText,
            answered: 0,
            optionCounts: new Map(),
          });
        }
        const bucket = byQuestion.get(qText);
        if (isAnswered(q)) {
          bucket.answered += 1;
          answerTokens(q).forEach((token) => {
            bucket.optionCounts.set(token, (bucket.optionCounts.get(token) || 0) + 1);
          });
        }
      });
    });
  });

  const participation = [...byQuestion.values()]
    .map((b) => ({
      question: b.question,
      answered: b.answered,
      total,
      rate: total > 0 ? (b.answered / total) * 100 : 0,
    }))
    .sort((a, b) => b.answered - a.answered);

  const topQuestions = participation.slice(0, 8);

  const responsesByQuestion = [...byQuestion.values()]
    .map((b) => ({
      question: b.question,
      totalAnswers: b.answered,
      options: [...b.optionCounts.entries()]
        .map(([name, value]) => ({ name, value }))
        .sort((a, c) => c.value - a.value)
        .slice(0, 12),
    }))
    .filter((q) => q.options.length > 0)
    .sort((a, b) => b.totalAnswers - a.totalAnswers);

  return { participation, topQuestions, responsesByQuestion, total };
}

export function buildRecentRows(filteredResults, limit = 10) {
  return [...filteredResults]
    .sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    })
    .slice(0, limit)
    .map((entry) => ({
      id: entry.id,
      surveyName: getEntryPrimarySurveyName(entry),
      location: getEntryLocationLabel(entry) || '—',
      timestamp: entry.timestamp,
      hasAudio: hasAudio(entry),
      entry,
    }));
}

export function formatPct(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

export function formatSatisfaction(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toFixed(1);
}

export function formatDurationSeconds(sec) {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return `${m}m ${String(r).padStart(2, '0')}s`;
}
